import type { UploadBuildInput } from "../contract.js";
import { api } from "./api.js";
import { credentialsReceived, loggedOut } from "./auth.slice.js";
import type { AppDispatch, RootState } from "./index.js";
import {
  uploadFailed,
  uploadProgressed,
  uploadStarted,
  uploadSucceeded,
  uploadVerifying,
  type UploadJob,
} from "./uploads.slice.js";

/**
 * Binaries are sent with XMLHttpRequest rather than through RTK Query, which
 * runs on `fetch`. A fetch request exposes no upload progress at all — the
 * promise simply settles when the server answers — so an operator pushing a
 * 200 MB IPA over an office uplink had no way to tell a slow upload from a
 * dead one. XHR is the only browser API that reports bytes sent.
 *
 * The request object also lives here, outside React: the transfer therefore
 * survives closing the dialog and navigating between pages, and is only
 * stopped by an explicit cancel or by leaving the site.
 */
const inFlight = new Map<string, XMLHttpRequest>();


interface XhrResult {
  status: number;
  body: string;
}

/** Thrown on `xhr.abort()` so a cancel is not reported to the operator as a failure. */
class UploadAborted extends Error {}

function describeFailure(status: number, body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === "object" && "error" in parsed) {
      const envelope = (parsed as { error?: { message?: unknown } }).error;
      if (envelope && typeof envelope.message === "string") return envelope.message;
    }
  } catch {
    // Not JSON: a proxy timeout page or an empty body. Fall through to the code.
  }
  if (status === 0) return "Koneksi ke server terputus saat mengunggah.";
  if (status === 413) return "Berkas melebihi batas ukuran unggah server.";
  return `Server menolak unggahan (HTTP ${status}).`;
}

function send(jobId: string, form: FormData, token: string, dispatch: AppDispatch): Promise<XhrResult> {
  return new Promise<XhrResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    inFlight.set(jobId, xhr);

    xhr.open("POST", "/v1/builds");
    xhr.setRequestHeader("authorization", `Bearer ${token}`);

    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return;
      dispatch(uploadProgressed({ id: jobId, loadedBytes: event.loaded }));
    });
    // Last byte handed to the socket; the server is now hashing and verifying.
    xhr.upload.addEventListener("load", () => {
      dispatch(uploadVerifying(jobId));
    });

    xhr.addEventListener("load", () => {
      inFlight.delete(jobId);
      resolve({ status: xhr.status, body: xhr.responseText });
    });
    xhr.addEventListener("error", () => {
      inFlight.delete(jobId);
      reject(new Error("Koneksi ke server terputus saat mengunggah."));
    });
    xhr.addEventListener("abort", () => {
      inFlight.delete(jobId);
      reject(new UploadAborted("cancelled"));
    });

    xhr.send(form);
  });
}

/**
 * Queues a binary and returns immediately; progress arrives as slice updates.
 * The caller may close its dialog on the next line.
 */
export function startBuildUpload(input: UploadBuildInput) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const jobId = crypto.randomUUID();
    const job: UploadJob = {
      id: jobId,
      filename: input.binary.name,
      appSlug: input.meta.app,
      channel: input.meta.channel,
      version: input.meta.version,
      sizeBytes: input.binary.size,
      loadedBytes: 0,
      status: "uploading",
      error: null,
      startedAt: Date.now(),
      finishedAt: null,
    };
    dispatch(uploadStarted(job));

    const form = new FormData();
    // `meta` before the file, as multer streams parts in order: the JSON must
    // already be parsed when the file handler needs the app and channel.
    form.append("meta", JSON.stringify(input.meta));
    form.append("binary", input.binary, input.binary.name);

    const token = getState().auth.accessToken;
    if (!token) {
      dispatch(uploadFailed({ id: jobId, error: "Sesi berakhir. Masuk kembali lalu ulangi unggahan." }));
      return;
    }

    try {
      let result = await send(jobId, form, token, dispatch);

      // Access tokens are short-lived and a large upload easily outlives one.
      // The bytes are already spent, so the retry is worth its cost exactly
      // once — beyond that the session is genuinely gone.
      if (result.status === 401) {
        const refreshToken = getState().auth.refreshToken;
        const session = refreshToken
          ? await dispatch(api.endpoints.refresh.initiate({ refreshToken })).unwrap().catch(() => null)
          : null;
        if (!session) {
          dispatch(loggedOut());
          dispatch(
            uploadFailed({ id: jobId, error: "Sesi berakhir. Masuk kembali lalu ulangi unggahan." }),
          );
          return;
        }
        dispatch(credentialsReceived(session));
        result = await send(jobId, form, session.accessToken, dispatch);
      }

      if (result.status >= 200 && result.status < 300) {
        dispatch(uploadSucceeded(jobId));
        // The build list and the audit trail both gained a row.
        dispatch(api.util.invalidateTags(["Build", "Audit"]));
        return;
      }

      dispatch(uploadFailed({ id: jobId, error: describeFailure(result.status, result.body) }));
    } catch (cause) {
      if (cause instanceof UploadAborted) return;
      dispatch(
        uploadFailed({ id: jobId, error: cause instanceof Error ? cause.message : String(cause) }),
      );
    }
  };
}

/** Aborts the transfer; the slice is moved to `cancelled` by the caller's dispatch. */
export function abortBuildUpload(jobId: string): void {
  inFlight.get(jobId)?.abort();
}

/**
 * A reload or tab close kills every in-flight request, and a half-sent 200 MB
 * binary is a long walk to repeat. The browser only honours this prompt after
 * the operator has interacted with the page, which uploading guarantees.
 */
window.addEventListener("beforeunload", (event) => {
  if (inFlight.size === 0) return;
  event.preventDefault();
});
